import { _ } from "../../core/polyglot/polyglot.js";
import { makeComputationApiRepository } from "../../core/repositories/repositoryBuilders.js";
import AccountingDocumentService from "../shared/documentInfrastructure/AccountingDocumentService.js";
import ForecastConfiguration from "../../models/forecast/template/ForecastConfiguration.js";
import ForecastTemplate from "../../models/forecast/template/ForecastTemplate.js";

/**
 * Service responsible for all data exchange required by the forecast
 * template archive document. The class coordinates other services
 * and repositories.
 *
 * @param {Session} session - Session singleton
 */
export default class ForecastTemplateArchiveDocumentService extends AccountingDocumentService {
	constructor({ session }) {
		super(...arguments);

		this._session = session;

		this._forecastTemplateRepository = makeComputationApiRepository({
			route: "ForecastTemplates/?id",
			model: ForecastTemplate,
			session,
		});

		this._forecastTemplateNameRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#id/Name",
			model: ForecastTemplate,
			session,
		});

		this._setMainForecastTemplateRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#id/Main",
			model: ForecastTemplate,
			session,
		});

		this._configurationRepository = makeComputationApiRepository({
			route: "DefaultForecastTemplateConfiguration",
			session,
		});

		this._forecastTemplateConfigurationRepository =
			makeComputationApiRepository({
				route: "ForecastTemplates/#forecastTemplateId/Configuration",
				model: ForecastConfiguration,
				session,
			});
	}

	/**
	 * Request the initial data required by the document.
	 */
	async fetchInitialData() {
		await super.fetchInitialData(...arguments);

		return {
			forecastTemplates: await this.fetchForecastTemplates(),
		};
	}

	fetchForecastTemplates() {
		return this._forecastTemplateRepository.get();
	}

	hasDefaultForecastConfiguration() {
		return this._configurationRepository.head();
	}

	async makeForecastTemplate({ model }) {
		const forecastTemplate = await this._forecastTemplateRepository.save({
			model: {
				chartOfAccountsId: this._session.getCurrentChartOfAccountsId(),
				...model,
			},
		});

		await this._updateForecastTemplateConfigurationDescription({
			forecastTemplate,
			description: model.description,
		});

		return forecastTemplate;
	}

	async copyForecastTemplate({ forecastTemplate, model }) {
		const newForecastRemplate = await this._forecastTemplateRepository.save(
			{
				model: {
					chartOfAccountsId:
						this._session.getCurrentChartOfAccountsId(),
					...model,
				},
				filter: { id: forecastTemplate.getId() },
				successMessage: _(
					"The forecast template has been copied successfully",
				),
				errorMessage: _("Failed to copy the forecast template"),
			},
		);

		await this._updateForecastTemplateConfigurationDescription({
			forecastTemplate: newForecastRemplate,
			description: model.description,
		});

		return newForecastRemplate;
	}

	deleteForecastTemplate(forecastTemplate) {
		return this._forecastTemplateRepository.remove({
			filter: { id: forecastTemplate.getId() },
		});
	}

	updateForecastTemplateName(forecastTemplate) {
		return this._forecastTemplateNameRepository.update({
			model: forecastTemplate.getName(),
			readResponse: false,
			filter: { id: forecastTemplate.getId() },
			successMessage: _(
				"The forecast template has been renamed successfully",
			),
			errorMessage: _("Failed to rename the forecast template"),
		});
	}

	setMainForecastTemplate(forecastTemplate) {
		return this._setMainForecastTemplateRepository.update({
			model: forecastTemplate.getId(),
			readResponse: false,
			filter: { id: forecastTemplate.getId() },
			successMessage: _(
				"The forecast template has been successfully set as main",
			),
			errorMessage: _("Failed to set forecast template as main"),
		});
	}

	async _updateForecastTemplateConfigurationDescription({
		forecastTemplate,
		description,
	}) {
		const forecastTemplateConfiguration =
			forecastTemplate.getConfiguration();

		forecastTemplateConfiguration?.setForecastTemplateDescription(
			description,
		);

		await this._updateForecastTemplateConfiguration(
			forecastTemplateConfiguration,
			forecastTemplate.getId(),
		);
	}

	_updateForecastTemplateConfiguration(
		forecastTemplateConfiguration,
		forecastTemplateId,
	) {
		return this._forecastTemplateConfigurationRepository.update({
			model: forecastTemplateConfiguration,
			readResponse: false,
			filter: { forecastTemplateId },
			successMessage: _("The configuration is successfully updated"),
			errorMessage: _("Failed to update the configuration"),
		});
	}
}
